import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { Storage } from '@ionic/storage';
import { BehaviorSubject, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { BasicService } from '../../services/basic.service';
import DateUtils from '../../utils/date.utils';
import { Choice } from '../select/choice.model';

@Injectable({
  providedIn: 'root',
})
export class SearchBarService {
  fetchSize = 20;
  referer$: any; // for endpoints with Content-Range header
  refererAsync$: any; // for async requests or endpoints without Content-Range header
  formatter: any;
  clearFiltersBS: BehaviorSubject<boolean> = new BehaviorSubject(false);
  filtersMap: Map<any, any> = new Map();
  /**
   * Is truthy only if at least one filter is set. The keys coming from the side bar filter are not included.
   */
  hasFilters: boolean;
  searchAndSortBS: BehaviorSubject<boolean | string> = new BehaviorSubject(false);
  sort = { active: null, direction: null };
  totalBS = new BehaviorSubject(0);
  itemsBS: BehaviorSubject<any[]> = new BehaviorSubject(null);

  customFilters: { [key: string]: Choice[] } = {
    yesNo: [
      { id: 'yes', label: this.basicService.translate('yes') },
      { id: 'no', label: this.basicService.translate('no') },
    ],
  };

  private lastIndex: number;
  private previousScrolltop = null;
  private subscriptions = new Subscription();

  constructor(
    private basicService: BasicService,
    private storage: Storage,
    private alcmeonContextService: AlcmeonContextService
  ) {
    this.init();
  }

  private init() {
    this.subscriptions.add(
      this.searchAndSortBS.subscribe(result => {
        if (!this.referer$ || !result) {
          return;
        }
        this.checkFilters();
        this.submitQuery();
      })
    );
    this.subscriptions.add(
      // TODO take aync mode in charge
      this.clearFiltersBS.subscribe(value => {
        if (value) {
          this.filtersMap.forEach((value, key) => {
            if (!['siteIds', 'siteGroupIds', 'siteTypes'].includes(key)) {
              this.filtersMap.delete(key);
            }
          });
        }
      })
    );
  }

  private checkFilters() {
    this.hasFilters = false;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.filtersMap.forEach((value, key) => {
      if (value && value.start !== undefined && value.end !== undefined) {
        if (value.start && value.end) {
          this.hasFilters = true;
        }
      } else if (
        !['siteIds', 'siteGroupIds', 'siteTypes'].includes(key) &&
        ((value instanceof Set && value.size) ||
          (Array.isArray(value) && value.length) ||
          (!(value instanceof Set) && !Array.isArray(value) && value))
      ) {
        this.hasFilters = true;
      }
    });
  }

  /**
   * Build criteria for sorting, paginating and filtering results
   */
  buildCriteria() {
    const fromEntries = function (map) {
      const entries = map.entries();
      const obj = {};
      for (const [key, value] of entries) {
        obj[key] = value;
      }
      return obj;
    };
    const criteria = fromEntries(this.filtersMap);
    Object.keys(criteria).forEach(key => {
      const { start } = criteria[key];
      if (start) {
        criteria[key] = DateUtils.formatDateRangePicker(criteria[key]);
      }
    });

    return {
      sort: this.sort,
      start: this.lastIndex ?? 0,
      range: this.fetchSize,
      ...criteria,
    };
  }

  sortData(event: Sort) {
    if (this.referer$) {
      this.sort = event;
      this.searchAndSortBS.next(true);
    }
  }

  /**
   * When scrolling horizontally on table ".content-purpose" :
   * - Keep table ".headers-purpose" scroll position synchro with table ".content-purpose".
   * - Make action buttons always visible and floating if necessary.
   */
  checkScrollPosition() {
    setTimeout(() => {
      const container: HTMLElement = document.querySelector('cdk-virtual-scroll-viewport');
      if (container) {
        const tbody: HTMLElement = document.querySelector('.content-purpose tbody');
        if (this.previousScrolltop === +container.scrollTop) {
          tbody.classList.add('dismiss');
        }

        const tableForHeadersOnly: HTMLElement = document.querySelector('.headers-purpose');
        tableForHeadersOnly.style.width = '0px';
        tableForHeadersOnly.style.width = `${container.offsetWidth}px`;
        tableForHeadersOnly.scrollLeft = container.scrollLeft;

        const scrollXEnded = container.scrollWidth - container.scrollLeft === container.clientWidth;
        (document.querySelectorAll('td.cdk-column-actions') || []).forEach(element => {
          if (scrollXEnded) {
            element.querySelector('.float-action').classList.add('scrolled-end');
          } else {
            element.querySelector('.float-action').classList.remove('scrolled-end');
          }
        });

        tbody.classList.remove('dismiss');
      }
    });
  }

  fetchItems({ scrollEvent }) {
    this.checkScrollPosition();
    const scrollableElement = scrollEvent.target;
    const { scrollHeight, scrollTop, clientHeight } = scrollableElement;

    if (scrollTop !== this.previousScrolltop && Math.floor(scrollHeight - scrollTop) <= clientHeight) {
      if (this.lastIndex === null || this.lastIndex > 0) {
        if (this.totalBS.value > this.fetchSize && this.lastIndex !== this.totalBS.value) {
          this.fetchItemsContent();
        }
      }
    }
    this.previousScrolltop = scrollTop;
  }

  submitQuery() {
    this.lastIndex = 0;
    this.fetchItemsContent(true);
    this.fetchItemsContentAsync();
  }

  private fetchItemsContent(reset = false) {
    if (!this.referer$) {
      return;
    }
    this.basicService.showSpinner();
    this.referer$()
      .pipe(
        finalize(() => {
          this.basicService.hideSpinner();
          setTimeout(() => {
            const searchBarInput = document.getElementById('input-search-bar');

            if (searchBarInput && searchBarInput['value']) {
              searchBarInput.dispatchEvent(new Event('focus'));
              searchBarInput.focus();
            }
          }, 300);
        })
      )
      .subscribe({
        next: (response: HttpResponse<any[]>) => {
          if (response.status !== 204) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const [value, from, to, total] = (response.headers.get('Content-Range') || 'record 0-0/0').match(
              /record (\d+)-(\d+)\/(\d+)/
            );
            this.lastIndex = +to + 1;

            this.totalBS.next(+total);
            const items = response.body;
            (items || []).forEach(this.formatter);

            if (reset) {
              if (items?.length === 0 && this.itemsBS.value?.length === 0) {
                this.checkScrollPosition();
                return;
              }
              this.itemsBS.next([].concat(items));
              this.checkScrollPosition();
            } else {
              this.itemsBS.next((this.itemsBS.value || []).concat(items));
            }
          } else {
            this.totalBS.next(0);
            if (reset) {
              this.itemsBS.next([]);
              this.checkScrollPosition();
            } else {
              this.itemsBS.next(this.itemsBS.value || []);
            }
          }
        },
      });
  }
  async applyFiltersFromLocaleStorageWithPrefixKey(key: string) {
    const filtersMap = await this.storage.get(`${this.alcmeonContextService.companyId}-${key}`);
    if (filtersMap) {
      this.filtersMap = filtersMap;
      this.checkFilters();
    }
  }

  saveFiltersInLocaleStorageWithPrefixKey(key: string) {
    this.storage.set(`${this.alcmeonContextService.companyId}-${key}`, this.filtersMap);
  }

  /**
   * Mecanism for requests not supporting HTTP headers Content-range (async requests or old endpoints).
   * Works without : pagination, server sorting and filtering.
   */
  private fetchItemsContentAsync() {
    if (!this.refererAsync$) {
      return;
    }
    this.basicService.showSpinner();
    this.refererAsync$()
      .pipe(finalize(() => this.basicService.hideSpinner()))
      .subscribe({
        next: (response: any[]) => {
          this.totalBS.next(response.length);
          const items = response;
          (items || []).forEach(this.formatter);
          this.itemsBS.next(items);
          this.checkScrollPosition();
        },
      });
  }

  onDidLeave() {
    this.referer$ = null;
    this.refererAsync$ = null;
    this.sort = { active: null, direction: null };
    this.formatter = null;
    this.itemsBS.next([]);
    this.totalBS.next(0);
    this.clearFiltersBS.next(true);
    this.filtersMap = new Map();
    this.hasFilters = false;

    this.subscriptions?.unsubscribe();
    this.subscriptions = new Subscription();
    this.clearFiltersBS = new BehaviorSubject(false);
    this.searchAndSortBS = new BehaviorSubject(false);

    this.init();
  }
}
